/**
 * Window Tool - Hosted Window Component Placement
 * 
 * Similar to doors but with sill height parameter.
 */

import { v4 as uuidv4 } from 'uuid';
import { WindowElement, WindowGeometryData, WallElement } from '../core/types';
import { TagElement } from './TagTool';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from './BaseTool';
import { familyManager } from '../core/FamilyManager';

export interface WindowToolOptions {
    windowType: string;
    width: number; // mm
    height: number; // mm
    sillHeight: number; // mm
    tagOnPlacement: boolean;
}

export class WindowTool extends BaseTool {
    id = 'window';
    name = 'Window';
    cursor = 'copy';

    private currentWall: WallElement | null = null;
    private currentPosition: number = 0; // 0-1

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.hostManager) return;
        if (event.button !== 0) return;

        // Resolve the host at click time (move events may not have fired over
        // the wall yet, e.g. right after tool activation).
        const host = this.hostManager.findHost(event.intersections, { categories: ['Walls'] })
            ?? this.hostManager.findHostByRay(this.context.scene, event.ray, { categories: ['Walls'] });

        if (host && host.isValid) {
            if (!this.currentWall || this.currentWall.id !== host.elementId) {
                const wall = this.context.bimStore.project.elements.get(host.elementId) as WallElement;
                if (!wall || !wall.geometryData?.start) return;
                this.currentWall = wall;
                this.currentPosition = this.projectOnWall(wall, host.point);
            }
            this.createWindowElement();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.hostManager) return;

        // Use HostManager to find a Wall. Event intersections only cover
        // meshes registered with R3F handlers, so fall back to a scene
        // raycast for BIM walls.
        const host = this.hostManager.findHost(event.intersections, { categories: ['Walls'] })
            ?? this.hostManager.findHostByRay(this.context.scene, event.ray, { categories: ['Walls'] });

        if (host && host.isValid) {
            const wall = this.context.bimStore.project.elements.get(host.elementId) as WallElement;
            if (!wall || !wall.geometryData?.start) {
                this.currentWall = null;
                this.context.appStore.setToolPreview(null);
                return;
            }

            this.currentWall = wall;
            this.currentPosition = this.projectOnWall(wall, host.point);

            this.updatePreview();
        } else {
            this.currentWall = null;
            this.context.appStore.setToolPreview(null);
        }
    }

    private projectOnWall(wall: WallElement, point: THREE.Vector3): number {
        const wallStart = new THREE.Vector3(...wall.geometryData.start);
        const wallEnd = new THREE.Vector3(...wall.geometryData.end);
        const wallLine = new THREE.Line3(wallStart, wallEnd);

        const closestPoint = new THREE.Vector3();
        wallLine.closestPointToPoint(point, true, closestPoint);

        const length = wallStart.distanceTo(wallEnd);
        if (length < 1e-6) return 0;
        const dist = wallStart.distanceTo(closestPoint);
        return Math.max(0, Math.min(1, dist / length));
    }

    onPointerUp(): void { }

    private flipFacing: boolean = false;

    onKeyDown(event: KeyboardEvent): void {
        if (event.code === 'Space') {
            this.flipFacing = !this.flipFacing;
            this.updatePreview();
        }
    }

    private updatePreview() {
        if (!this.context || !this.currentWall) return;

        this.context.appStore.setToolPreview({
            type: 'window',
            wallId: this.currentWall.id,
            position: this.currentPosition,
            width: 0.915,
            sillHeight: 0.9,
            flipFacing: this.flipFacing
        });
    }

    private createWindowElement() {
        if (!this.context || !this.currentWall) return;

        // Default window type
        const windowType = 'Fixed: 0915 x 1830mm';
        const [familyName, typeName] = windowType.split(': ').map(s => s.trim());
        const typeSymbol = familyManager.getType(familyName, typeName);

        let widthM = 0.915;
        let heightM = 1.830;

        if (typeSymbol) {
            if (typeSymbol.parameters['Width']) widthM = typeSymbol.parameters['Width'] as number;
            if (typeSymbol.parameters['Height']) heightM = typeSymbol.parameters['Height'] as number;
        }

        const sillHeightM = 0.915;

        const geometryData: WindowGeometryData = {
            hostWallId: this.currentWall.id,
            position: this.currentPosition,
            width: widthM,
            height: heightM,
            sillHeight: sillHeightM
        };

        const window: WindowElement = {
            id: uuidv4(),
            category: 'Windows',
            type: windowType,
            parameters: {
                'Width': `${widthM.toFixed(2)} m`,
                'Height': `${heightM.toFixed(2)} m`,
                'Sill Height': `${sillHeightM.toFixed(2)} m`,
                'Host': this.currentWall.id,
                'Level': this.currentWall.parameters['Base Constraint'],
                'Flip Facing': this.flipFacing,
                'Glass Type': typeSymbol?.parameters['Glass Type'] || 'Double Pane',
                'Frame Material': typeSymbol?.parameters['Frame Material'] || 'Aluminum'
            },
            constraints: [
                {
                    type: 'HOST_CONTAINED',
                    targetId: this.currentWall.id,
                    data: { positionAlongHost: this.currentPosition }
                }
            ],
            geometryData
        };

        this.context.bimStore.addElement(window);

        // Honor "Tag on Placement" option: attach a window tag on the wall at
        // the window's position along its length.
        if (this.context.bimStore.toolProperties.tagOnPlacement && this.currentWall.geometryData?.start && this.currentWall.geometryData?.end) {
            const wallStart = new THREE.Vector3(...this.currentWall.geometryData.start);
            const wallEnd = new THREE.Vector3(...this.currentWall.geometryData.end);
            const tagPos = wallStart.clone().lerp(wallEnd, this.currentPosition);
            tagPos.z += sillHeightM + heightM / 2; // window center height

            const tag: TagElement = {
                id: uuidv4(),
                category: 'Tags',
                type: 'Tag: Windows',
                parameters: {
                    'Target Category': 'Windows',
                    'Target Id': window.id,
                    'Value': window.type,
                    'Parameter': 'Type Mark'
                },
                constraints: [
                    { type: 'HOST_CONTAINED', targetId: window.id, data: {} }
                ],
                geometryData: {
                    position: [tagPos.x, tagPos.y, tagPos.z],
                    targetId: window.id
                }
            };
            this.context.bimStore.addElement(tag);
        }
    }
}

